import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Seo from '../components/Seo';
import InquiryForm from '../components/InquiryForm';
import RichContent from '../components/RichContent';
import ProductGallery from '../components/ProductGallery';
import SpecsTable from '../components/SpecsTable';
import Faqs from '../components/Faqs';
import RelatedProducts from '../components/RelatedProducts';
import { api } from '../lib/api';
import { categoryName } from '../config/brand';

export default function ProductDetailPage() {
  const { sku } = useParams<{ sku: string }>();
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', sku],
    queryFn: () => api.getProduct(sku!),
    enabled: !!sku,
  });

  if (isLoading) return <div className="container-page py-20 text-center text-slate-500">Loading…</div>;
  if (error || !product) {
    return (
      <div className="container-page py-20 text-center">
        <h1 className="font-display text-2xl font-bold">Product not found</h1>
        <Link to="/products" className="btn-primary mt-6 inline-flex">Back to products</Link>
      </div>
    );
  }

  // Primary image + any configured gallery images, de-duplicated.
  const galleryImages = [
    product.imageUrl,
    ...(product.galleryImages ?? []),
  ].filter((v, i, a): v is string => !!v && a.indexOf(v) === i);

  const hasRichEn = !!product.descriptionEnHtml?.trim();
  const hasRichHi = !!product.descriptionHiHtml?.trim();

  // Compose JSON-LD: a Product entity + optional FAQPage when FAQs are
  // configured. Search engines can consume both in a single script tag.
  const jsonLd: Array<Record<string, unknown>> = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      sku: product.rowKey,
      description: product.tagline || product.descriptionEn || undefined,
      image: galleryImages.length > 0 ? galleryImages : undefined,
      brand: { '@type': 'Brand', name: 'Uclipse' },
      category: product.partitionKey,
    },
  ];
  if ((product.faqs ?? []).length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: product.faqs!.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return (
    <>
      <Seo
        title={product.name}
        description={product.tagline ?? product.descriptionEn}
        image={product.imageUrl}
        seoTitle={product.seoTitle}
        seoDescription={product.seoDescription}
        seoImageUrl={product.seoImageUrl}
        canonicalUrl={product.canonicalUrl}
        jsonLd={jsonLd}
      />

      {/* Hero / buy-box band */}
      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-brand-50 via-white to-emerald-50/40">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-100/40 blur-3xl" />
        <div className="container-page relative py-8 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-600">Home</Link>
            <span className="text-slate-300">/</span>
            <Link to="/products" className="hover:text-brand-600">Products</Link>
            <span className="text-slate-300">/</span>
            <Link to={`/products?category=${product.partitionKey}`} className="hover:text-brand-600">
              {categoryName(product.partitionKey)}
            </Link>
            <span className="text-slate-300">/</span>
            <span className="font-medium text-slate-700">{product.name}</span>
          </nav>

          <div className="mt-6 grid gap-10 md:grid-cols-2 md:gap-12">
            <div className="md:sticky md:top-24 md:self-start">
              {galleryImages.length > 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <ProductGallery images={galleryImages} alt={product.name} />
                </div>
              ) : (
                <div className="grid aspect-square place-items-center rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
                  <span className="font-display text-6xl font-bold text-slate-200">{product.rowKey}</span>
                </div>
              )}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  {categoryName(product.partitionKey)}
                </span>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                  SKU {product.rowKey}
                </span>
                {product.featured && (
                  <span className="rounded-full bg-accent-green/10 px-3 py-1 text-xs font-semibold text-green-700 ring-1 ring-accent-green/30">
                    Featured
                  </span>
                )}
              </div>

              <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
                {product.name}
              </h1>
              {product.tagline && <p className="mt-3 text-lg text-slate-600">{product.tagline}</p>}

              {/* Trust strip */}
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                {['ZED Silver Certified', 'Made in India', 'Bulk & Private-Label Ready'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5">
                    <svg className="h-4 w-4 text-accent-green" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {t}
                  </span>
                ))}
              </div>

              {/* Sizes */}
              <div className="mt-7">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Available pack sizes</h3>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <span
                      key={s}
                      className="rounded-lg border border-brand-100 bg-white px-3.5 py-1.5 text-sm font-semibold text-brand-700 shadow-sm"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              {/* Applications */}
              {product.applications.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ideal for</h3>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {product.applications.map((a) => (
                      <span
                        key={a}
                        className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-accent-green" aria-hidden="true" />
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA card */}
              <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm text-slate-600">
                  Get institutional pricing, MOQ and packaging options. We respond within 1 business day.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a href="#enquire" className="btn-primary">Request a Quote</a>
                  {product.datasheetUrl && (
                    <a href={product.datasheetUrl} target="_blank" rel="noopener" className="btn-secondary">
                      Download Datasheet
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview / description */}
      {(hasRichEn || product.descriptionEn || hasRichHi || product.descriptionHi) && (
        <section className="container-page py-12 md:py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-display text-2xl font-bold text-slate-900 md:text-3xl">Product overview</h2>
            <div className="mt-5">
              {hasRichEn ? (
                <RichContent html={product.descriptionEnHtml!} />
              ) : (
                product.descriptionEn && (
                  <p className="whitespace-pre-line text-lg leading-relaxed text-slate-700">{product.descriptionEn}</p>
                )
              )}
              {hasRichHi ? (
                <div className="mt-6">
                  <RichContent html={product.descriptionHiHtml!} />
                </div>
              ) : (
                product.descriptionHi && (
                  <p className="mt-4 whitespace-pre-line text-lg leading-relaxed text-slate-700">{product.descriptionHi}</p>
                )
              )}
            </div>
          </div>
        </section>
      )}

      {/* Why you'll love it — highlights */}
      {(product.highlights ?? []).length > 0 && (
        <section className="border-t border-slate-100 py-12 md:py-16">
          <div className="container-page">
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-sm font-semibold uppercase tracking-wide text-accent-green">Why you&apos;ll love it</span>
              <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 md:text-3xl">
                Made to perform — and to trust
              </h2>
            </div>
            <div className="mx-auto mt-10 grid max-w-5xl gap-5 sm:grid-cols-2">
              {product.highlights!.map((h, i) => (
                <div
                  key={i}
                  className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-accent-green/10 text-green-700">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-slate-900">{h.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{h.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What's inside — ingredients */}
      {(product.ingredients ?? []).length > 0 && (
        <section className="border-t border-slate-100 bg-gradient-to-b from-emerald-50/50 to-white py-12 md:py-16">
          <div className="container-page">
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-sm font-semibold uppercase tracking-wide text-accent-green">What&apos;s inside</span>
              <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 md:text-3xl">
                Full ingredient transparency
              </h2>
              <p className="mt-3 text-slate-600">Every ingredient, and exactly what it does.</p>
            </div>
            <div className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {product.ingredients!.map((ing, i) => (
                <div key={i} className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-accent-green" aria-hidden="true" />
                    <h3 className="font-display text-sm font-semibold text-slate-900">{ing.title}</h3>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{ing.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How to use */}
      {(product.howToUse ?? []).length > 0 && (
        <section className="border-t border-slate-100 py-12 md:py-16">
          <div className="container-page mx-auto max-w-3xl">
            <div className="text-center">
              <span className="text-sm font-semibold uppercase tracking-wide text-accent-green">How to use</span>
              <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 md:text-3xl">
                Simple, everyday ritual
              </h2>
            </div>
            <ol className="mt-8 space-y-4">
              {product.howToUse!.map((step, i) => (
                <li key={i} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-brand-500 font-display text-sm font-bold text-white">
                    {i + 1}
                  </span>
                  <p className="pt-1 text-slate-700">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {(product.specs ?? []).length > 0 && (
        <section className="border-t border-slate-100 bg-slate-50/60 py-12 md:py-16">
          <div className="container-page mx-auto max-w-3xl">
            <h2 className="mb-5 font-display text-2xl font-bold text-slate-900 md:text-3xl">
              At a glance
            </h2>
            <SpecsTable specs={product.specs!} />
          </div>
        </section>
      )}

      {(product.faqs ?? []).length > 0 && (
        <section className="container-page py-12 md:py-16">
          <div className="mx-auto max-w-3xl">
            <h2 className="mb-5 font-display text-2xl font-bold text-slate-900 md:text-3xl">
              Questions, answered
            </h2>
            <Faqs items={product.faqs!} />
          </div>
        </section>
      )}

      <RelatedProducts skus={product.relatedSkus ?? []} />

      <section id="enquire" className="bg-slate-50 py-16">
        <div className="container-page mx-auto max-w-2xl">
          <InquiryForm
            type="quote"
            productSku={product.rowKey}
            title={`Request quote: ${product.name}`}
            description="Tell us your required quantity, delivery city, and timeline. We respond within 1 business day."
          />
        </div>
      </section>
    </>
  );
}
